import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Header } from '../components/Header';
import { useAuth } from '../context/useAuth';
import { useAdmin } from '../context/useAdmin';
import { useQueryNav } from '../lib/nav';
import { api, ApiError } from '../lib/api';
import type { KbArticle, KbCategory, KbTerm, KbTermInput } from '../lib/types';

/**
 * The assistant's reference shelf.
 *
 * Two kinds of knowledge, kept apart because they are used differently:
 *
 *   Terms     one line each — what NPT, ROP or BHA means. A term reaches the
 *             model when a question names it.
 *   Articles  what does not fit in a line — how to read a DDR, what the NPT
 *             codes mean on this contract. An article reaches the model when a
 *             question is about it, even if the words differ.
 *
 * Everyone signed in may read; only the superadmin may change anything. The
 * server enforces that — the read-only view here is a courtesy.
 */

type Tab = 'terms' | 'articles';

function errorText(err: unknown, fallback: string) {
  return err instanceof ApiError ? err.message : fallback;
}

export function KnowledgeBase() {
  const { user } = useAuth();
  const { access } = useAdmin();
  const editable = user?.role === 'superadmin' && !access.preview;

  const { params, set } = useQueryNav();
  const tab: Tab = params.get('tab') === 'articles' ? 'articles' : 'terms';

  return (
    <div className="app">
      <Header context="wells" />

      <main className="kbpage">
        <div className="kbhead">
          <h1 className="kbtitle">Knowledge Base</h1>
          <p className="kbintro">
            What the assistant knows about the business before it reads a single report. Terms say
            what a word means; articles explain how things are done here. Neither is ever quoted as a
            well's value, numbers come from the documents.
          </p>

          <div className="kbtabs" role="tablist">
            {(['terms', 'articles'] as const).map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                className={tab === t ? 'kbtab is-active' : 'kbtab'}
                onClick={() => set({ tab: t === 'terms' ? null : t, article: null })}
              >
                {t === 'terms' ? 'Terms' : 'Articles'}
              </button>
            ))}
          </div>

          {!editable && (
            <p className="kbreadonly">Read only — only a superadmin can change the knowledge base.</p>
          )}
        </div>

        {tab === 'terms' ? <Terms editable={editable} /> : <Articles editable={editable} />}
      </main>
    </div>
  );
}

/* ----------------------------------------------------------------- terms */

const EMPTY_TERM: KbTermInput = {
  term: '',
  abbreviation: '',
  categorySlug: '',
  definition: '',
  unit: '',
  aliases: [],
};

function Terms({ editable }: { editable: boolean }) {
  const [terms, setTerms] = useState<KbTerm[] | null>(null);
  const [categories, setCategories] = useState<KbCategory[]>([]);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');

  // null: no form open. '' : a new term. A slug: that term.
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<KbTermInput>(EMPTY_TERM);
  const [aliases, setAliases] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    api.kbCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  // A short pause after typing, so each keystroke is not its own request.
  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      api
        .kbTerms(query.trim(), category)
        .then((items) => {
          if (!cancelled) setTerms(items);
        })
        .catch((err) => {
          if (!cancelled) {
            setTerms([]);
            setError(errorText(err, 'Could not load the terms.'));
          }
        });
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, category, reloads]);

  const open = (t: KbTerm | null) => {
    setError(null);
    setConfirmDelete(false);
    setEditing(t ? t.slug : '');
    setDraft(
      t
        ? {
            term: t.term,
            abbreviation: t.abbreviation ?? '',
            categorySlug: t.categorySlug ?? '',
            definition: t.definition,
            unit: t.unit ?? '',
            aliases: t.aliases ?? [],
          }
        : EMPTY_TERM,
    );
    setAliases((t?.aliases ?? []).join(', '));
  };

  const close = () => {
    setEditing(null);
    setConfirmDelete(false);
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || editing === null) return;

    setBusy(true);
    setError(null);

    const body: KbTermInput = {
      ...draft,
      aliases: aliases
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean),
    };

    try {
      if (editing === '') await api.kbCreateTerm(body);
      else await api.kbUpdateTerm(editing, body);
      close();
      setReloads((n) => n + 1);
    } catch (err) {
      setError(errorText(err, 'Could not save the term.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!editing || busy) return;

    setBusy(true);
    try {
      await api.kbDeleteTerm(editing);
      close();
      setReloads((n) => n + 1);
    } catch (err) {
      setError(errorText(err, 'Could not delete the term.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="kbpanel">
      <div className="kbtools">
        <input
          className="kbsearch"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search terms, abbreviations, definitions…"
          aria-label="Search terms"
        />
        <select
          className="kbselect"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          aria-label="Category"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.slug} value={c.slug}>
              {c.name}
            </option>
          ))}
        </select>
        <span className="kbcount">{terms ? `${terms.length} terms` : ''}</span>
        {editable && (
          <button type="button" className="kbbtn kbbtn--primary" onClick={() => open(null)}>
            + New term
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert--page" role="alert">
          {error}
        </div>
      )}

      {editing !== null && (
        <form className="kbform" onSubmit={save}>
          <h2 className="kbform-title">{editing === '' ? 'New term' : `Edit ${draft.term}`}</h2>

          <div className="kbgrid">
            <label>
              Term
              <input
                value={draft.term}
                onChange={(e) => setDraft({ ...draft, term: e.target.value })}
                placeholder="Non-Productive Time"
                required
                autoFocus
              />
            </label>
            <label>
              Abbreviation
              <input
                value={draft.abbreviation}
                onChange={(e) => setDraft({ ...draft, abbreviation: e.target.value })}
                placeholder="NPT"
              />
            </label>
            <label>
              Category
              <select
                value={draft.categorySlug}
                onChange={(e) => setDraft({ ...draft, categorySlug: e.target.value })}
              >
                <option value="">—</option>
                {categories.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Unit
              <input
                value={draft.unit}
                onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
                placeholder="hr"
              />
            </label>
          </div>

          <label>
            Definition
            <textarea
              value={draft.definition}
              onChange={(e) => setDraft({ ...draft, definition: e.target.value })}
              rows={3}
              required
              placeholder="Time lost to failures, waiting or repairs, counted in hours."
            />
          </label>

          <label>
            Other names <small>(comma separated — the assistant matches these too)</small>
            <input
              value={aliases}
              onChange={(e) => setAliases(e.target.value)}
              placeholder="downtime, lost time"
            />
          </label>

          <div className="kbform-actions">
            <button type="submit" className="kbbtn kbbtn--primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="kbbtn" onClick={close}>
              Cancel
            </button>

            <span className="kbspacer" />

            {editing !== '' &&
              (confirmDelete ? (
                <>
                  <span className="kbconfirm">Delete this term?</span>
                  <button type="button" className="kbbtn kbbtn--danger" onClick={() => void remove()} disabled={busy}>
                    Delete
                  </button>
                  <button type="button" className="kbbtn" onClick={() => setConfirmDelete(false)}>
                    Keep
                  </button>
                </>
              ) : (
                <button type="button" className="kbbtn kbbtn--danger" onClick={() => setConfirmDelete(true)}>
                  Delete…
                </button>
              ))}
          </div>
        </form>
      )}

      {terms === null && <p className="content-loading">Loading…</p>}
      {terms?.length === 0 && <p className="content-loading">No terms match.</p>}

      {terms && terms.length > 0 && (
        <table className="kbtable">
          <thead>
            <tr>
              <th>Term</th>
              <th>Abbr.</th>
              <th>Category</th>
              <th>Definition</th>
            </tr>
          </thead>
          <tbody>
            {terms.map((t) => (
              <tr
                key={t.slug}
                className={editable ? 'is-clickable' : undefined}
                onClick={editable ? () => open(t) : undefined}
              >
                <td className="kbterm">{t.term}</td>
                <td>{t.abbreviation ? <code>{t.abbreviation}</code> : '—'}</td>
                <td className="kbmuted">{t.categoryName ?? '—'}</td>
                <td>
                  {t.definition}
                  {t.unit && <span className="kbmuted"> ({t.unit})</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/* -------------------------------------------------------------- articles */

function Articles({ editable }: { editable: boolean }) {
  const { params, set } = useQueryNav();

  const [articles, setArticles] = useState<KbArticle[] | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const selectedId = Number(params.get('article')) || 0;
  const selected = useMemo(
    () => articles?.find((a) => a.id === selectedId) ?? null,
    [articles, selectedId],
  );

  const load = useCallback(() => {
    api
      .kbArticles()
      .then(setArticles)
      .catch((err) => {
        setArticles([]);
        setError(errorText(err, 'Could not load the articles.'));
      });
  }, []);

  useEffect(load, [load]);

  // Opening an article — from the list, or from a citation in the chat —
  // fills the editor with it.
  useEffect(() => {
    if (selected) {
      setCreating(false);
      setTitle(selected.title);
      setBody(selected.body);
      setConfirmDelete(false);
      setNotice(null);
    }
  }, [selected]);

  const pick = (id: number) => {
    setError(null);
    set({ article: String(id) });
  };

  const startNew = () => {
    setError(null);
    setNotice(null);
    setCreating(true);
    setTitle('');
    setBody('');
    set({ article: null });
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;

    setBusy(true);
    setError(null);
    setNotice(null);

    try {
      const saved = creating
        ? await api.kbCreateArticle({ title, body })
        : await api.kbUpdateArticle(selectedId, { title, body });

      setNotice(
        saved.embedded
          ? 'Saved. The assistant can use it from the next question.'
          : 'Saved, but the model provider did not respond — the assistant will pick it up after the API restarts.',
      );
      setCreating(false);
      load();
      set({ article: String(saved.id) }, { replace: true });
    } catch (err) {
      setError(errorText(err, 'Could not save the article.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!selected || busy) return;

    setBusy(true);
    try {
      await api.kbDeleteArticle(selected.id);
      setConfirmDelete(false);
      set({ article: null }, { replace: true });
      setTitle('');
      setBody('');
      load();
    } catch (err) {
      setError(errorText(err, 'Could not delete the article.'));
    } finally {
      setBusy(false);
    }
  };

  const showEditor = creating || selected !== null;

  return (
    <section className="kbpanel kbsplit">
      <aside className="kblist" aria-label="Articles">
        {editable && (
          <button type="button" className="kbbtn kbbtn--primary kbnew" onClick={startNew}>
            + New article
          </button>
        )}

        {articles === null && <p className="content-loading">Loading…</p>}
        {articles?.length === 0 && <p className="content-loading">No articles yet.</p>}

        {articles?.map((a) => (
          <button
            key={a.id}
            type="button"
            className={a.id === selectedId ? 'kbitem is-selected' : 'kbitem'}
            onClick={() => pick(a.id)}
          >
            <span className="kbitem-title">{a.title}</span>
            <span className="kbitem-meta">
              {a.embedded ? 'Ready for the assistant' : 'Not yet readable by the assistant'}
            </span>
          </button>
        ))}
      </aside>

      <div className="kbeditor">
        {error && (
          <div className="alert alert--page" role="alert">
            {error}
          </div>
        )}
        {notice && <p className="kbnotice">{notice}</p>}

        {!showEditor && (
          <p className="content-loading">
            {editable
              ? 'Pick an article to edit it, or write a new one. Good articles explain one thing: how to read a report, what a set of codes means, how the crew names things.'
              : 'Pick an article to read it.'}
          </p>
        )}

        {showEditor && (
          <form className="kbform kbform--article" onSubmit={save}>
            <label>
              Title
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="How NPT is coded on this contract"
                maxLength={200}
                required
                readOnly={!editable}
                autoFocus={creating}
              />
            </label>

            <label className="kbbody">
              Article
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Write it as you would explain it to a new engineer on the rig."
                maxLength={20000}
                required
                readOnly={!editable}
              />
              <small className="kbmuted">{body.length.toLocaleString()} / 20,000 characters</small>
            </label>

            {editable && (
              <div className="kbform-actions">
                <button type="submit" className="kbbtn kbbtn--primary" disabled={busy}>
                  {busy ? 'Saving…' : 'Save'}
                </button>

                <span className="kbspacer" />

                {!creating &&
                  selected &&
                  (confirmDelete ? (
                    <>
                      <span className="kbconfirm">Delete “{selected.title}”?</span>
                      <button
                        type="button"
                        className="kbbtn kbbtn--danger"
                        onClick={() => void remove()}
                        disabled={busy}
                      >
                        Delete
                      </button>
                      <button type="button" className="kbbtn" onClick={() => setConfirmDelete(false)}>
                        Keep
                      </button>
                    </>
                  ) : (
                    <button type="button" className="kbbtn kbbtn--danger" onClick={() => setConfirmDelete(true)}>
                      Delete…
                    </button>
                  ))}
              </div>
            )}
          </form>
        )}
      </div>
    </section>
  );
}
